import React from 'react';
import * as echarts from 'echarts';
import { mathFormat } from '@sg/utils/common';
import { chartGradientColors, chartGradientNOColors } from '@sg/utils/useChartOptions';

const DISPLAY_ZERO_THRESHOLD = 50_000;

const normalizeTrendValue = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return value;
  }
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return null;
  }
  return Math.abs(numericValue) < DISPLAY_ZERO_THRESHOLD ? 0 : numericValue;
};

export const processRevenueTrendData = (
  data: any[],
  showChatsType: any,
  showRevenueTrendList: string[],
  formatMessage: any,
  activeCard: string,
  showEventBubbles: boolean = false
) => {
  const chartData: any = {};
  if (showRevenueTrendList.length === 0) return chartData;

  showRevenueTrendList.forEach((item) => {
    if (item === 'table') {
      chartData[item] = {
        data,
        columns: [
          {
            header: 'Month',
            dataIndex: 'month',
            key: 'month',
            width: 100,
          },
          {
            header: 'Monthly Actual',
            dataIndex: 'actual',
            key: 'Actual',
            width: 100,
            format: mathFormat,
            render: (item: any) => <div style={{ textAlign: 'right', width: '105px', textWrap: 'nowrap' }}>{mathFormat(item?.actual) || '-'}</div>
          },
          {
            header: 'Monthly Forecast',
            dataIndex: 'forecast',
            key: 'Forecast',
            width: 100,
            format: mathFormat,
            render: (item: any) => <div style={{ textAlign: 'right', width: '116px', textWrap: 'nowrap' }}>{mathFormat(item?.forecast) || '-'}</div>
          },
          {
            header: 'Monthly KPI',
            dataIndex: 'kpi',
            key: 'KPI',
            width: 100,
            format: mathFormat,
            render: (item: any) => <div style={{ textAlign: 'right', width: '87px', textWrap: 'nowrap' }}>{mathFormat(item?.kpi) || '-'}</div>
          }
        ],
      };
      return;
    }

    const xAxisData: any = [];
    const datas: any = {};
    // Store per-index projects for tooltip: monthly for bar, cumulative for line
    const projectsPerIndex: any[] = [];

    // Calculate cumulative values for line chart, use monthly values for bar chart
    if (showChatsType?.value === 'line') {
      // For line chart: calculate cumulative (YTD) values
      let cumulativeActual: number | null = null;
      let cumulativeForecast: number | null = null;
      let cumulativeKpi: number | null = null;
      let lastActualValue: number | null = null;
      let lastActualIndex: number | null = null;
      // Track cumulative project values: { [project_id]: { ...proj, actual: cumSum, kpi: cumSum } }
      const cumulativeProjectsMap: Record<string, any> = {};

      datas.Actual = [];
      datas.Forecast = [];
      datas.KPI = [];

      data.forEach((element: any, idx: number) => {
        xAxisData.push(element.month);

        // Accumulate project values across months
        const monthProjects = element.projects || [];
        monthProjects.forEach((proj: any) => {
          const pid = proj.project_id;
          if (!pid) return;
          const projValue = proj.actual ?? proj.forecast ?? null;
          const projKpi = proj.kpi ?? null;
          if (cumulativeProjectsMap[pid]) {
            if (projValue !== null) {
              cumulativeProjectsMap[pid].actual = (cumulativeProjectsMap[pid].actual ?? 0) + projValue;
            }
            if (projKpi !== null) {
              cumulativeProjectsMap[pid].kpi = (cumulativeProjectsMap[pid].kpi ?? 0) + projKpi;
            }
          } else {
            cumulativeProjectsMap[pid] = {
              ...proj,
              actual: projValue,
              kpi: projKpi,
            };
          }
        });

        // Snapshot current cumulative projects, sorted by descending actual value
        const cumulativeProjectsSnapshot = Object.values(cumulativeProjectsMap)
          .map((p: any) => ({ ...p }))
          .sort((a: any, b: any) => (b.actual ?? 0) - (a.actual ?? 0));
        projectsPerIndex.push(cumulativeProjectsSnapshot);

        // 处理 Actual 和 Forecast 的互斥关系
        // 如果 actual 存在，则 Actual 有值，Forecast 在该位置为 null
        if (element.actual !== null && element.actual !== undefined) {
          if (cumulativeActual === null) {
            cumulativeActual = element.actual;
          } else {
            cumulativeActual += element.actual;
          }
          datas.Actual.push(normalizeTrendValue(cumulativeActual));
          datas.Forecast.push(null);  // Forecast 在该位置为 null
          lastActualValue = cumulativeActual;
          lastActualIndex = idx;
        }
        // 如果只有 forecast 存在，则 Forecast 有值，Actual 在该位置为 null
        else if (element.forecast !== null && element.forecast !== undefined) {
          if (cumulativeActual === null) {
            cumulativeActual = lastActualValue ?? 0;
          }
          cumulativeActual += element.forecast;
          datas.Actual.push(null);  // Actual 在该位置为 null
          datas.Forecast.push(normalizeTrendValue(cumulativeActual));
        }
        // 如果都没有数据
        else {
          cumulativeActual = null;
          cumulativeForecast = null;
          datas.Actual.push(null);
          datas.Forecast.push(null);
        }

        // 处理 KPI
        if (element.kpi === null) {
          cumulativeKpi = null;
        } else if (cumulativeKpi === null) {
          cumulativeKpi = element.kpi;
        } else {
          cumulativeKpi += element.kpi;
        }

        datas.KPI.push(normalizeTrendValue(cumulativeKpi));
      });

      // 确保 Actual 和 Forecast 在最后一个 Actual 位置连接
      if (lastActualIndex !== null && lastActualIndex >= 0 && lastActualValue !== null) {
        const forecastValue = datas.Forecast[lastActualIndex];
        if (forecastValue === null || forecastValue === undefined) {
          datas.Forecast[lastActualIndex] = normalizeTrendValue(lastActualValue);
        }
      }
    } else if (showChatsType?.value === 'bar') {
      // For bar chart: use monthly values
      datas.Actual = [];
      datas.Forecast = [];
      datas.KPI = [];

      data.forEach((element: any) => {
        xAxisData.push(element.month);
        datas.Actual.push(normalizeTrendValue(element.actual));
        datas.Forecast.push(normalizeTrendValue(element.forecast));
        datas.KPI.push(normalizeTrendValue(element.kpi));
        projectsPerIndex.push(element.projects || []);
      });
    }

    xAxisData.push('');

    // Build series data array
    const seriesData = Object.keys(datas).map((keys, index) => {
      const tdata: any = {
        name: formatMessage({ id: `dbs_charts_legend_${keys.toLowerCase()}` }),
        barGap: showChatsType?.value === 'bar' ? '-100%' : '0%',
        barWidth: '25%',
        data: datas[keys].map((items: any, i: number) => {
          if (showChatsType?.value === 'bar' && keys !== 'KPI') {
            if (datas?.Actual[i]) {
              return {
                value: items,
                names: activeCard,
                projects: projectsPerIndex[i] || [],
                itemStyle: {
                  color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: chartGradientColors[index]?.[1] },
                    { offset: 1, color: chartGradientColors[index]?.[0] }
                  ]),
                },
                label: {
                  position: datas?.Actual[i] > 0 ? 'top' : 'bottom',
                },
              };
            }
            return {
              value: items,
              names: activeCard,
              projects: projectsPerIndex[i] || [],
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: chartGradientNOColors[index]?.[1] },
                  { offset: 1, color: chartGradientNOColors[index]?.[0] }
                ]),
              }
            };
          }
          const titleNameobj: any = {
            Profit: 'Profit',
            Revenue: 'Gross Revenue',
          };
          if (keys === 'Actual' && showChatsType?.value === 'line') {
            // Flag when actual is the largest series at that month (keeps label tight to dot)
            const isDominantActual =
              datas?.Actual[i] > datas.KPI[i] && datas?.Actual[i] > datas.Forecast[i];
            // Treat normalized values (0.0M) as zero so their labels stay above the axis
            const isZeroValue = Number(items) === 0;
            return {
              ...items,
              label: {
                position: datas?.Actual[i] >= 0 ? 'top' : 'bottom',
                // the logic here has been simplified because actual and forcast line chart has merged together (meaning no much overlapping might happen)
                // but keep this logic for future enhancement (DO NOT DELETE IT)
                // distance: isZeroValue ? 12 : (isDominantActual ? 0 : -25), 
              },
              value: items,
              names: `YTM ${titleNameobj[activeCard]}`,
              projects: projectsPerIndex[i] || [],
            };
          }
          return items;
        }),
        type: keys === 'KPI' && showChatsType?.value === 'bar' ? 'line' : showChatsType?.value,
        z: showChatsType?.value === 'bar' ? keys === 'KPI' ? 99 : Object.keys(datas).length - index : keys === 'Actual' ? 99 : Object.keys(datas).length - index,
        showSymbol: showChatsType?.format,
      };

      if (showChatsType?.value === 'line' || showChatsType?.value === 'bar') {
        tdata.lineStyle = {
          type: showChatsType?.value === 'bar' || keys === 'Actual' ? '' : 'dashed',
          width: 2,
        };
        tdata.label = {
          show: keys === 'Actual' ? showChatsType?.format : false,
          formatter: (params: any) => mathFormat(params?.value),
          rich: {
            color: 'red',
            d: {
              color: '#fff',
              fontSize: 16,
              lineHeight: 24,
              height: 24
            },
          },
          textStyle: {
            color: '#262832',
            fontSize: 12,
          },
        };
      }
      return tdata;
    });

    // Add TopTipsEvent series for event positioning (hidden series used by EventWindows)
    // This must be added to seriesData to ensure it exists when chart initializes and convertToPixel is called
    if (showEventBubbles) {
      const xAxisDataLength = xAxisData.length;
      const isBarChart = showChatsType?.value === 'bar';
      seriesData.push({
        name: 'TopTipsEvent',
        type: isBarChart ? 'bar' : 'line',
        ...(isBarChart ? {
          barWidth: '25%',
          barGap: '-100%',
        } : {}),
        data: new Array(xAxisDataLength).fill(0),
        itemStyle: { color: 'transparent', opacity: 0 },
        lineStyle: { opacity: 0 },
        label: { show: false },
        tooltip: { show: false },
        silent: true,
        z: -1,
        hiddenFromLegend: true,
      });
    }

    chartData[item] = {
      xAxisData,
      type: showChatsType?.value,
      seriesData
    };
  });

  return chartData;
};

